import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { useToast } from '../../ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { devicesService, CircuitInProgressError } from '../../../services/devices';
import type { BoardCircuitSession } from '../../../types/device';
import type { BoardHold } from '../../../types/climb';
import type { BoardLighting } from './useBoardLighting';
import { boardHoldRole } from './roles';
import CircuitPlayerBar from '../CircuitPlayerBar';
import { useCircuitRun } from '../circuit-run';

interface CircuitPlayerProps {
  gymSlug: string;
  boardId: string;
  climbId: string;
  holds: BoardHold[];
  /**
   * How this wall is lit, shared with the lightbulb above the photo (see
   * `useBoardLighting`). With no transport the player still walks the circuit on
   * the photo, but there is no wall to light and nothing to claim.
   */
  lighting: BoardLighting;
  /** The hold the run is on, so the photo above can point at the same one. */
  onCurrentChange?: (index: number | null) => void;
  /** The run reached the top — time to ask for a send. */
  onFinish?: () => void;
}

/**
 * Plays a circuit on a board: the shared bar and run (see `CircuitPlayerBar` and
 * `useCircuitRun`) with the wall wired to them. Each step lights the whole problem
 * and leaves the hold to go to next flashing — the flashing itself is the device's
 * to run, this only says which hold and when.
 *
 * A wall driven by a device holds one run at a time, so starting takes a claim on
 * the board (BoardCircuitSession): another climber's run has to be interrupted on
 * purpose, and the claim is kept alive by a ping for as long as this page is open.
 * An app that closes mid-run therefore frees the board by falling silent — and if
 * this run is the one interrupted, the ping is how it finds out.
 *
 * A board lit from this phone's own radio needs none of that: a Moonboard talks to
 * one phone at a time, so the Bluetooth link *is* the claim, and the wall says no
 * to the second climber without anyone having to ask the server. There the
 * flashing is ours to run too, frame by frame — see `board-bluetooth.ts`.
 */
export default function CircuitPlayer({
  gymSlug,
  boardId,
  climbId,
  holds,
  lighting,
  onCurrentChange,
  onFinish,
}: CircuitPlayerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [claiming, setClaiming] = useState(false);
  // Who has the board, while the climber decides whether to cut in on them.
  const [conflict, setConflict] = useState<CircuitInProgressError | null>(null);
  const [session, setSession] = useState<BoardCircuitSession | null>(null);

  const canLight = lighting.transport !== null;
  // Only a shared wall is claimed on the server; see the note above.
  const claimsBoard = lighting.transport === 'device';

  // Best-effort, like the light button on the profile: the command leaving says
  // nothing about what the LEDs did, and a run on screen is worth keeping even
  // when the wall stops answering.
  const light = useCallback(
    (blinkHold: number | null, release: boolean) => {
      if (!canLight) return;

      lighting
        .light(climbId, { blinkHold, keepAlive: !release, circuitStep: true })
        .catch((error: unknown) => {
          toast({
            title: t('climbs.board.lightError'),
            description: error instanceof Error ? error.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
        });
    },
    [canLight, lighting, climbId, toast, t]
  );

  // Gives the claim back. Kept in a ref as well as in state because the release
  // also runs from a cleanup, long after the render that held it.
  const sessionRef = useRef<BoardCircuitSession | null>(null);

  const releaseSession = useCallback(() => {
    const held = sessionRef.current;
    sessionRef.current = null;
    setSession(null);
    // Fire and forget: the claim expires by itself anyway, and a token that has
    // already been taken over is one the server leaves alone.
    if (held) devicesService.stopCircuitSession(gymSlug, boardId, held.token).catch(() => {});
  }, [boardId, gymSlug]);

  const run = useCircuitRun({
    holds,
    roleOf: boardHoldRole,
    // A start hold is lit like the rest but never flashes: the climber is already
    // on it. The server enforces the same rule.
    onEnter: (index) => {
      onCurrentChange?.(index);
      light(holds[index].role === 'start' ? null : index, false);
    },
    // The wall goes still: the circuit stays lit and the board stays ours.
    onPause: () => light(null, false),
    // The wall keeps the problem lit, but the link goes back: a board nobody lets
    // go of is unreachable for the next climber. `silent` is the one case where the
    // board is not ours to hand back — another climber has taken it and is already
    // lighting it.
    //
    // On this phone's own link there is no link to hand back here: the flashing
    // stops, the problem stays up, and the board is let go of when the page
    // leaves it (see `useBoardLighting`) — the climber is still standing at it.
    onStop: ({ silent }) => {
      onCurrentChange?.(null);
      if (!silent) light(null, true);
      releaseSession();
    },
    onFinish,
  });

  // Takes the board and starts the run. A board that cannot be lit is nobody's to
  // claim, and neither is one this phone lights itself — the Bluetooth link the
  // first step opens is what keeps the next climber off it. Those runs simply
  // begin, which also keeps the chooser inside the click that asked for it.
  const start = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (claimsBoard) {
        setClaiming(true);
        try {
          const claimed = await devicesService.startCircuitSession(gymSlug, boardId, climbId, {
            force,
          });
          sessionRef.current = claimed;
          setSession(claimed);
        } catch (error) {
          if (error instanceof CircuitInProgressError) {
            setConflict(error);
            return;
          }
          toast({
            title: t('climbs.board.circuit.startError'),
            description: error instanceof Error ? error.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
          return;
        } finally {
          setClaiming(false);
        }
      }

      setConflict(null);
      run.begin();
    },
    [boardId, claimsBoard, climbId, gymSlug, run, t, toast]
  );

  // The claim only lives while this page says it is still here. Well inside the
  // window it was given, so one missed ping is not enough to lose the board.
  const stopRun = run.stop;
  useEffect(() => {
    if (!run.running || !session) return;

    const interval = setInterval(async () => {
      const held = await devicesService
        .pingCircuitSession(gymSlug, boardId, session.token)
        // A ping that never arrived says nothing about who owns the board; the
        // claim outlives a couple of them.
        .catch(() => true);
      if (held) return;

      // Somebody interrupted this run, and is already lighting the wall — so stop,
      // and leave their circuit alone.
      stopRun({ silent: true });
      toast({ title: t('climbs.board.circuit.interrupted'), variant: 'destructive' });
    }, (session.ttl_seconds / 3) * 1000);

    return () => clearInterval(interval);
  }, [run.running, session, gymSlug, boardId, stopRun, toast, t]);

  // Nothing to walk: an empty problem, or one with nothing in it but feet.
  if (!run.walkable) return null;

  return (
    <>
      <CircuitPlayerBar
        run={run}
        // Which hand the hold being flashed is taken with, on a circuit that
        // forces them. The wall says it in colour; the bar says it in a word.
        currentHand={run.current === null ? null : (holds[run.current]?.hand ?? null)}
        onStart={() => void start()}
        starting={claiming}
      />

      {/* Somebody else is on this wall. Their name and their problem are here
          because the climber can look over and see whether they have finished. */}
      <Dialog open={!!conflict} onOpenChange={(open) => !open && setConflict(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('climbs.board.circuit.busyTitle')}</DialogTitle>
            <DialogDescription>
              {conflict?.userName
                ? t('climbs.board.circuit.busyDescription', {
                    user: conflict.userName,
                    climb: conflict.climbName,
                  })
                : t('climbs.board.circuit.busyDescriptionUnknown')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConflict(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void start({ force: true })}
              disabled={claiming}
            >
              {t('climbs.board.circuit.interrupt')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
